import { useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import useStore from "../store/useStore";
import { useAllShows, useShowById } from "../hooks/useShows";
import StarRating from "../components/StarRating";
import Poster from "../components/Poster";
import { ArrowLeft, Lock, Globe, Users, EyeOff, Eye } from "lucide-react";
import { toast } from "sonner";
import { TOAST_MS } from "../lib/constants";
import { createLog } from "../lib/services";
import { formatApiError } from "../lib/api";
import { lower } from "../lib/strings";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Log() {
  const { showId: paramShowId } = useParams();
  const navigate = useNavigate();
  const refreshSaves = useStore((s) => s.refreshSaves);
  const allShows = useAllShows();

  const [showId, setShowId] = useState(paramShowId || "");
  const [step, setStep] = useState(paramShowId ? 1 : 0);
  const [date, setDate] = useState("today");
  const [exactDate, setExactDate] = useState(todayISO());
  const [stars, setStars] = useState(0);
  const [note, setNote] = useState("");
  const [hasSpoilers, setHasSpoilers] = useState(false);
  const [visibility, setVisibility] = useState("public");
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const selectedShow = useShowById(showId);

  const filteredShows = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return allShows;
    return allShows.filter((s) =>
      lower(s.title).includes(q) ||
      lower(s.theatre).includes(q) ||
      lower(s.director).includes(q)
    );
  }, [search, allShows]);

  const submit = async () => {
    if (!showId) {
      toast("pick a show first");
      return;
    }
    // map UI date label to ISO date string (or null when "i forget")
    const computeDateSeen = () => {
      if (date === "i forget") return null;
      if (date === "other date") return exactDate;
      if (date === "yesterday") {
        return new Date(Date.now() - 86400000).toISOString().slice(0, 10);
      }
      return todayISO();
    };
    const dateSeen = computeDateSeen();

    setSubmitting(true);
    try {
      await createLog({
        showId,
        dateSeen,
        rating: stars || null,
        note: note.trim(),
        hasSpoilers,
        visibility,
      });
      // server unsaves on log creation; refresh local cache
      await refreshSaves();
      toast("logged", { duration: TOAST_MS.NORMAL });
      navigate("/profile");
    } catch (err) {
      toast(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="log-page" className="min-h-screen bg-black text-white pb-[calc(10rem+env(safe-area-inset-bottom))]">
      <header className="px-5 pt-6 pb-4 border-b-2 border-white/15">
        <button
          onClick={() => navigate(-1)}
          className="font-mono text-xs lowercase text-white/60 hover:text-white inline-flex items-center gap-1.5"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> back
        </button>
        <h1 className="font-display text-4xl sm:text-5xl leading-none mt-3">
          log a show<span className="text-acid">.</span>
        </h1>
      </header>

      {/* step 0: choose show */}
      {step === 0 && (
        <section data-testid="log-step-choose-show" className="px-5 py-5">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
            which show?
          </h2>
          <input
            data-testid="show-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="search title, theatre, director"
            className="w-full bg-transparent border-2 border-white/30 px-3 py-3 font-mono text-sm placeholder:text-white/30 focus:border-acid outline-none"
          />
          <ul className="mt-4 max-h-[55vh] overflow-y-auto border border-white/10">
            {filteredShows.map((s) => (
              <li key={s.id}>
                <button
                  data-testid={`pick-show-${s.id}`}
                  onClick={() => { setShowId(s.id); setStep(1); }}
                  className="w-full text-left flex items-center gap-3 border-b border-white/10 px-3 py-3 hover:bg-acid/10 transition-colors"
                >
                  <Poster show={s} size="sm" />
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-base leading-tight truncate">{lower(s.title)}</div>
                    <div className="font-mono text-[10px] text-white/50 truncate">
                      {lower(s.theatre)} · {lower(s.dateLabel)}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
          <Link
            to="/add"
            className="mt-4 inline-block font-mono text-[11px] uppercase tracking-wider text-white/40 underline underline-offset-4"
          >
            add manually
          </Link>
        </section>
      )}

      {/* steps 1+: form */}
      {step >= 1 && selectedShow && (
        <>
          <section className="px-5 py-4 border-b-2 border-white/15 flex items-center gap-3">
            <Poster show={selectedShow} size="sm" />
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                show
              </div>
              <div className="font-display text-xl leading-tight truncate">
                {lower(selectedShow.title)}
              </div>
              <div className="font-mono text-[10px] text-white/50 truncate">
                {lower(selectedShow.theatre)}
              </div>
              {!paramShowId && (
                <button
                  onClick={() => setStep(0)}
                  className="mt-1 font-mono text-[10px] uppercase tracking-wider text-white/40 underline"
                >
                  change
                </button>
              )}
            </div>
          </section>

          {/* date */}
          <section className="px-5 py-5 border-b-2 border-white/15">
            <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
              when?
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {["today", "yesterday", "other date", "i forget"].map((d) => (
                <button
                  key={d}
                  data-testid={`date-${d.replace(/\s/g,"-")}`}
                  onClick={() => setDate(d)}
                  className={`font-mono text-xs lowercase border-2 py-3 transition-colors ${
                    date === d ? "border-acid bg-acid text-black" : "border-white/40 text-white hover:border-white"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
            {date === "other date" && (
              <input
                data-testid="exact-date-input"
                type="date"
                value={exactDate}
                onChange={(e) => setExactDate(e.target.value)}
                className="mt-3 w-full bg-transparent border-2 border-white/30 px-3 py-2.5 font-mono text-sm focus:border-acid outline-none"
              />
            )}
          </section>

          {/* stars — prominent */}
          <section className="px-5 py-7 border-b-2 border-white/15">
            <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">
              your rating
            </h2>
            <StarRating
              value={stars}
              onChange={setStars}
              size={44}
              testIdPrefix="star"
            />
            <div className="mt-3 flex items-center gap-3">
              {stars > 0 ? (
                <span className="font-mono text-xs text-acid tabular-nums">
                  {stars.toFixed(1)} / 5
                </span>
              ) : (
                <span className="font-mono text-[11px] text-white/40">tap to rate (half stars ok)</span>
              )}
              {stars > 0 && (
                <button
                  onClick={() => setStars(0)}
                  className="font-mono text-[10px] uppercase text-white/40 underline"
                >
                  clear
                </button>
              )}
            </div>
          </section>

          {/* note */}
          <section className="px-5 py-5 border-b-2 border-white/15">
            <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
              note
            </h2>
            <textarea
              data-testid="note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              placeholder="optional"
              className="w-full bg-transparent border-2 border-white/30 px-3 py-3 font-mono text-sm placeholder:text-white/30 focus:border-acid outline-none resize-none"
            />
            <label className="mt-3 inline-flex items-center gap-2 cursor-pointer select-none" data-testid="spoiler-toggle-label">
              <input
                type="checkbox"
                data-testid="spoiler-toggle"
                checked={hasSpoilers}
                onChange={(e) => setHasSpoilers(e.target.checked)}
                className="sr-only"
              />
              <span
                aria-hidden
                className={`w-4 h-4 border-2 ${hasSpoilers ? "bg-acid border-acid" : "border-white/40"} flex items-center justify-center`}
              >
                {hasSpoilers && (
                  <span className="text-black text-[10px] leading-none">✓</span>
                )}
              </span>
              <span className="font-mono text-xs text-white/80 inline-flex items-center gap-1">
                {hasSpoilers ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                contains spoilers
              </span>
            </label>
          </section>

          {/* visibility */}
          <section className="px-5 py-5 border-b-2 border-white/15">
            <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
              visibility
            </h2>
            <div className="grid grid-cols-3 gap-2">
              <button
                data-testid="visibility-public"
                onClick={() => setVisibility("public")}
                className={`font-mono text-[11px] lowercase border-2 py-3 transition-colors flex items-center justify-center gap-1.5 ${
                  visibility === "public" ? "border-acid bg-acid text-black" : "border-white/40 text-white hover:border-white"
                }`}
              >
                <Globe className="w-3.5 h-3.5" /> public
              </button>
              <button
                data-testid="visibility-followers"
                onClick={() => setVisibility("followers")}
                className={`font-mono text-[11px] lowercase border-2 py-3 transition-colors flex items-center justify-center gap-1.5 ${
                  visibility === "followers" ? "border-acid bg-acid text-black" : "border-white/40 text-white hover:border-white"
                }`}
              >
                <Users className="w-3.5 h-3.5" /> followers
              </button>
              <button
                data-testid="visibility-private"
                onClick={() => setVisibility("private")}
                className={`font-mono text-[11px] lowercase border-2 py-3 transition-colors flex items-center justify-center gap-1.5 ${
                  visibility === "private" ? "border-acid bg-acid text-black" : "border-white/40 text-white hover:border-white"
                }`}
              >
                <Lock className="w-3.5 h-3.5" /> private
              </button>
            </div>
          </section>

          {/* submit */}
          <section className="px-5 py-6">
            <button
              data-testid="submit-log"
              onClick={submit}
              disabled={submitting}
              className="w-full bg-acid text-black font-mono text-sm uppercase tracking-wider py-4 border-2 border-acid hover:bg-black hover:text-acid transition-colors disabled:opacity-50"
            >
              {submitting ? "posting..." : "post"}
            </button>
          </section>
        </>
      )}
    </main>
  );
}
